import { useEffect, useRef, useState, useCallback } from 'react';
import type { AuctionPlayer, AuctionResourceType, AuctionTrade, AuctionPhase } from '@/pages/auction/AuctionPage';
import { usePlayerIcons } from '@/hooks/usePlayerIcons';
import './AuctionArena.css';

// Generate a unique trade ID
const generateTradeId = (): string => {
  const timestamp = Date.now();
  const randomPart = Math.random().toString(36).substring(2, 11);
  return `trade_${timestamp}_${randomPart}`;
};

// Player colors map
const PLAYER_COLORS: Record<string, string> = {
  red: '#FF4444',
  blue: '#4488FF',
  green: '#44FF44',
  yellow: '#FFFF44',
  purple: '#AA44FF',
  orange: '#FF8844',
  cyan: '#44FFFF',
  pink: '#FF44AA',
};

interface AuctionArenaProps {
  resource: AuctionResourceType;
  resourceConfig: {
    id: AuctionResourceType;
    name: string;
    icon: string;
    emoji: string;
    basePrice: number;
    minPrice: number;
    maxPrice: number;
  };
  players: AuctionPlayer[];
  storeInventory: Record<AuctionResourceType, number>;
  storePrices: {
    buy: Record<AuctionResourceType, number>;
    sell: Record<AuctionResourceType, number>;
  };
  currentPlayerId: string;
  phase: AuctionPhase;
  timeRemaining?: number;
  serverDriven?: boolean;
  onArenaClick: (price: number, isTopHalf: boolean) => void;
  onTradeExecuted: (trade: AuctionTrade) => void;
}

// Walking speed (price units per second)
const WALK_SPEED = 25;

// Trade proximity threshold (price units) - player bars must overlap
const TRADE_PROXIMITY = 2;

// How close a bar must be to a store line before the store deals
const STORE_LINE_PROXIMITY = 1.25;

// One unit changes hands this often while a pair stays together
const FILL_MS = 400;

export default function AuctionArena({
  resource,
  resourceConfig,
  players,
  storeInventory,
  storePrices,
  currentPlayerId,
  phase,
  timeRemaining = 0,
  serverDriven = false,
  onArenaClick,
  onTradeExecuted,
}: AuctionArenaProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(Date.now());

  const activePairRef = useRef<string | null>(null);
  const touchedAtRef = useRef<Map<string, number>>(new Map());
  const lastFillRef = useRef<Map<string, number>>(new Map());
  const storeBuyLatchRef = useRef<Set<string>>(new Set());
  const pendingGoodsRef = useRef<Map<string, number>>(new Map());
  const pendingSpendRef = useRef<Map<string, number>>(new Map());
  const pendingSoldRef = useRef(0);
  const pendingInboundRef = useRef(0);
  const seenGoodsRef = useRef<Map<string, number>>(new Map());
  const seenMoneyRef = useRef<Map<string, number>>(new Map());
  const seenStockRef = useRef(0);
  const lastStockRef = useRef<Map<string, number>>(new Map());
  const countFlashRef = useRef<Map<string, { delta: number; at: number }>>(new Map());

  // Local player positions for smooth animation
  const [playerPositions, setPlayerPositions] = useState<Map<string, {
    current: number;
    target: number;
    isWalking: boolean;
  }>>(new Map());
  
  // Load player icons
  const playerIcons = usePlayerIcons();
  const [playerIconImages, setPlayerIconImages] = useState<Map<number, HTMLImageElement>>(new Map());
  
  // Canvas dimensions
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
  
  // Price range
  const minPrice = resourceConfig.minPrice;
  const maxPrice = resourceConfig.maxPrice;
  const priceRange = maxPrice - minPrice;
  
  // Store prices for current resource
  const storeBuyPrice = storePrices.buy[resource];
  const storeSellPrice = storePrices.sell[resource];
  const storeStock = storeInventory[resource];
  const chartRef = useRef({ min: minPrice, max: maxPrice });
  chartRef.current = { min: minPrice, max: maxPrice };
  const playersRef = useRef(players);
  const positionsRef = useRef(playerPositions);
  const onTradeRef = useRef(onTradeExecuted);
  const storeLineRef = useRef({ buy: storeBuyPrice, sell: storeSellPrice, stock: storeStock });
  playersRef.current = players;
  positionsRef.current = playerPositions;
  onTradeRef.current = onTradeExecuted;
  storeLineRef.current = { buy: storeBuyPrice, sell: storeSellPrice, stock: storeStock };
  
  // Margins for the arena
  const MARGIN_TOP = 80;
  const MARGIN_BOTTOM = 80;
  const MARGIN_LEFT = 80;
  const MARGIN_RIGHT = 40;
  
  // Load player icon images
  useEffect(() => {
    const loadImages = async () => {
      const images = new Map<number, HTMLImageElement>();
      
      for (const [playerNum, dataUrl] of Object.entries(playerIcons)) {
        const img = new Image();
        img.src = dataUrl;
        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
        images.set(Number(playerNum), img);
      }
      
      setPlayerIconImages(images);
    };
    
    if (Object.keys(playerIcons).length > 0) {
      loadImages();
    }
  }, [playerIcons]);
  
  // Calculate Y position from price
  const priceToY = useCallback((price: number): number => {
    const arenaHeight = dimensions.height - MARGIN_TOP - MARGIN_BOTTOM;
    const normalized = (price - minPrice) / priceRange;
    // Invert Y: higher prices at top
    return MARGIN_TOP + arenaHeight * (1 - normalized);
  }, [dimensions.height, minPrice, priceRange]);
  
  // Calculate price from Y position
  const yToPrice = useCallback((y: number): number => {
    const arenaHeight = dimensions.height - MARGIN_TOP - MARGIN_BOTTOM;
    const normalized = 1 - (y - MARGIN_TOP) / arenaHeight;
    return Math.round(minPrice + normalized * priceRange);
  }, [dimensions.height, minPrice, priceRange]);
  
  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.max(600, rect.width),
          height: Math.max(400, Math.min(600, rect.height)),
        });
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);
  
  // Sync player positions with props (important for AI movement)
  useEffect(() => {
    setPlayerPositions(prev => {
      const newPositions = new Map<string, { current: number; target: number; isWalking: boolean }>();
      
      players.forEach(player => {
        const existing = prev.get(player.playerId);
        const snapToPrice = !player.isWalking && player.currentPrice === player.targetPrice;

        newPositions.set(player.playerId, {
          current: serverDriven || snapToPrice ? player.currentPrice : (existing?.current ?? player.currentPrice),
          target: player.targetPrice,
          isWalking: serverDriven
            ? player.isWalking
            : snapToPrice
              ? false
              : existing
                ? Math.abs(existing.current - player.targetPrice) > 1
                : player.isWalking,
        });
      });
      
      return newPositions;
    });
  }, [players, serverDriven]);
  
  // A new commodity starts a fresh trading book
  useEffect(() => {
    activePairRef.current = null;
    touchedAtRef.current.clear();
    lastFillRef.current.clear();
    storeBuyLatchRef.current.clear();
    pendingGoodsRef.current.clear();
    pendingSpendRef.current.clear();
    pendingSoldRef.current = 0;
    pendingInboundRef.current = 0;
    seenGoodsRef.current.clear();
    seenMoneyRef.current.clear();
    seenStockRef.current = storeLineRef.current.stock;
    lastStockRef.current.clear();
    countFlashRef.current.clear();
  }, [resource]);
  
  // Animation loop for walking
  useEffect(() => {
    const animate = () => {
      const now = Date.now();
      const deltaTime = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;
      
      setPlayerPositions(prev => {
        const newPositions = new Map(prev);
        
        const { min, max } = chartRef.current;
        const store = storeLineRef.current;
        const roleOf = (playerId: string) => playersRef.current.find((player) => player.playerId === playerId)?.role;
        newPositions.forEach((pos, playerId) => {
          const role = roleOf(playerId);
          const floor = role === 'seller' ? Math.max(min, store.buy) : min;
          const ceiling = role === 'buyer' ? Math.min(max, store.sell) : max;
          const clamp = (value: number) => Math.max(floor, Math.min(ceiling, value));
          const target = clamp(pos.target);
          const current = clamp(pos.current);
          if (Math.abs(current - target) > 0.5) {
            const direction = target > current ? 1 : -1;
            const distance = Math.abs(target - current);
            const step = Math.min(WALK_SPEED * deltaTime, distance);
            
            const newCurrent = current + direction * step;
            
            if (Math.abs(newCurrent - target) < 0.5) {
              newPositions.set(playerId, {
                ...pos,
                current: target,
                target,
                isWalking: false,
              });
            } else {
              newPositions.set(playerId, {
                ...pos,
                current: clamp(newCurrent),
                target,
                isWalking: true,
              });
            }
          } else if (current !== pos.current || target !== pos.target) {
            newPositions.set(playerId, { ...pos, current, target, isWalking: false });
          }
        });
        
        return newPositions;
      });
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  
  // The first bars that meet keep trading. The store sells at the top line and buys out the bottom line.
  useEffect(() => {
    if (serverDriven || phase !== 'trading') {
      activePairRef.current = null;
      return;
    }

    const goodsOf = (player: AuctionPlayer) => {
      const prop = Number(player.resources?.[resource] || 0);
      const seen = seenGoodsRef.current.get(player.playerId);
      let pending = pendingGoodsRef.current.get(player.playerId) || 0;
      if (seen != null && prop < seen) {
        pending = Math.max(0, pending - (seen - prop));
        pendingGoodsRef.current.set(player.playerId, pending);
      }
      seenGoodsRef.current.set(player.playerId, prop);
      return Math.max(0, prop - pending);
    };

    const moneyOf = (player: AuctionPlayer) => {
      const prop = player.money || 0;
      const seen = seenMoneyRef.current.get(player.playerId);
      let pending = pendingSpendRef.current.get(player.playerId) || 0;
      if (seen != null && prop !== seen) {
        pending -= seen - prop;
        pendingSpendRef.current.set(player.playerId, pending);
      }
      seenMoneyRef.current.set(player.playerId, prop);
      return prop - pending;
    };

    const stockLeft = () => Math.max(
      0,
      storeLineRef.current.stock - pendingSoldRef.current + pendingInboundRef.current,
    );

    const spend = (playerId: string, amount: number) => {
      pendingSpendRef.current.set(playerId, (pendingSpendRef.current.get(playerId) || 0) + amount);
    };

    const emit = (trade: Omit<AuctionTrade, 'id' | 'timestamp' | 'resource'>) => {
      onTradeRef.current({
        id: generateTradeId(),
        resource,
        timestamp: new Date().toISOString(),
        ...trade,
      });
    };

    const checkInterval = setInterval(() => {
      const now = Date.now();
      const list = playersRef.current;
      const positions = positionsRef.current;
      const store = storeLineRef.current;
      const priceOf = (playerId: string) => positions.get(playerId)?.current;

      if (store.stock !== seenStockRef.current) {
        if (store.stock < seenStockRef.current) {
          pendingSoldRef.current = Math.max(0, pendingSoldRef.current - (seenStockRef.current - store.stock));
        } else {
          pendingInboundRef.current = Math.max(0, pendingInboundRef.current - (store.stock - seenStockRef.current));
        }
        seenStockRef.current = store.stock;
      }

      const inMarket = (price: number) => price >= store.buy - 0.05 && price <= store.sell + 0.05;
      const onBuyLine = (price: number) => price <= store.buy + STORE_LINE_PROXIMITY && price >= store.buy - 0.05;
      const onSellLine = (price: number) => price >= store.sell - STORE_LINE_PROXIMITY && price <= store.sell + 0.05;

      for (const seller of list) {
        if (seller.role !== 'seller') continue;
        const price = priceOf(seller.playerId);
        if (price == null || !onBuyLine(price)) storeBuyLatchRef.current.delete(seller.playerId);
      }

      type Deal = {
        key: string;
        kind: 'pair' | 'store-sell' | 'store-buy';
        buyer?: AuctionPlayer;
        seller?: AuctionPlayer;
        price: number;
      };
      const deals: Deal[] = [];
      for (const buyer of list) {
        if (buyer.role !== 'buyer') continue;
        const buyerPrice = priceOf(buyer.playerId);
        if (buyerPrice == null || !inMarket(buyerPrice)) continue;
        for (const seller of list) {
          if (seller.role !== 'seller' || goodsOf(seller) <= 0) continue;
          const sellerPrice = priceOf(seller.playerId);
          if (sellerPrice == null || !inMarket(sellerPrice)) continue;
          if (Math.abs(buyerPrice - sellerPrice) >= TRADE_PROXIMITY) continue;
          if (moneyOf(buyer) < Math.round((buyerPrice + sellerPrice) / 2)) continue;
          deals.push({
            key: `${buyer.playerId}|${seller.playerId}`,
            kind: 'pair',
            buyer,
            seller,
            price: Math.round((buyerPrice + sellerPrice) / 2),
          });
        }
        if (onSellLine(buyerPrice) && stockLeft() > 0 && moneyOf(buyer) >= Math.round(store.sell)) {
          deals.push({
            key: `${buyer.playerId}|store`,
            kind: 'store-sell',
            buyer,
            price: Math.round(store.sell),
          });
        }
      }
      for (const seller of list) {
        if (seller.role !== 'seller' || storeBuyLatchRef.current.has(seller.playerId)) continue;
        const price = priceOf(seller.playerId);
        if (price == null || !onBuyLine(price) || goodsOf(seller) <= 0) continue;
        deals.push({
          key: `store|${seller.playerId}`,
          kind: 'store-buy',
          seller,
          price: Math.round(store.buy),
        });
      }

      const stillTouching = new Set(deals.map((deal) => deal.key));
      let stamp = now;
      for (const deal of deals) {
        if (!touchedAtRef.current.has(deal.key)) touchedAtRef.current.set(deal.key, stamp++);
      }
      for (const key of [...touchedAtRef.current.keys()]) {
        if (!stillTouching.has(key)) touchedAtRef.current.delete(key);
      }
      deals.sort((a, b) => (touchedAtRef.current.get(a.key) || 0) - (touchedAtRef.current.get(b.key) || 0));

      let active = activePairRef.current;
      let activeDeal = active ? deals.find((deal) => deal.key === active) : undefined;
      if (active && !activeDeal) {
        activePairRef.current = null;
        active = null;
      }
      if (!active && deals.length > 0) {
        active = deals[0].key;
        activePairRef.current = active;
        activeDeal = deals[0];
        lastFillRef.current.set(active, 0);
      }
      if (!activeDeal) return;

      const last = lastFillRef.current.get(activeDeal.key) || 0;
      if (now - last < FILL_MS) return;
      lastFillRef.current.set(activeDeal.key, now);

      if (activeDeal.kind === 'pair' && activeDeal.buyer && activeDeal.seller) {
        pendingGoodsRef.current.set(
          activeDeal.seller.playerId,
          (pendingGoodsRef.current.get(activeDeal.seller.playerId) || 0) + 1,
        );
        spend(activeDeal.buyer.playerId, activeDeal.price);
        spend(activeDeal.seller.playerId, -activeDeal.price);
        emit({
          buyerId: activeDeal.buyer.playerId,
          buyerName: activeDeal.buyer.username,
          sellerId: activeDeal.seller.playerId,
          sellerName: activeDeal.seller.username,
          price: activeDeal.price,
          quantity: 1,
          isStoreTransaction: false,
        });
        return;
      }

      if (activeDeal.kind === 'store-sell' && activeDeal.buyer) {
        pendingSoldRef.current += 1;
        spend(activeDeal.buyer.playerId, activeDeal.price);
        emit({
          buyerId: activeDeal.buyer.playerId,
          buyerName: activeDeal.buyer.username,
          sellerId: 'store',
          sellerName: 'Store',
          price: activeDeal.price,
          quantity: 1,
          isStoreTransaction: true,
        });
        return;
      }

      if (activeDeal.kind === 'store-buy' && activeDeal.seller) {
        const qty = goodsOf(activeDeal.seller);
        if (qty <= 0) {
          activePairRef.current = null;
          return;
        }
        storeBuyLatchRef.current.add(activeDeal.seller.playerId);
        pendingGoodsRef.current.set(activeDeal.seller.playerId, (pendingGoodsRef.current.get(activeDeal.seller.playerId) || 0) + qty);
        spend(activeDeal.seller.playerId, -(activeDeal.price * qty));
        pendingInboundRef.current += qty;
        activePairRef.current = null;
        emit({
          buyerId: 'store',
          buyerName: 'Store',
          sellerId: activeDeal.seller.playerId,
          sellerName: activeDeal.seller.username,
          price: activeDeal.price,
          quantity: qty,
          isStoreTransaction: true,
        });
      }
    }, 50);

    return () => clearInterval(checkInterval);
  }, [phase, resource, serverDriven]);
  
  const playerIsTrading = (playerId: string, _price: number, _role: AuctionPlayer['role']) => {
    const active = activePairRef.current;
    return !!active && active.split('|').includes(playerId);
  };

  // Draw the arena
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, dimensions.width, dimensions.height);
    
    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, dimensions.height);
    gradient.addColorStop(0, '#2a1a4a'); // Top - sellers (purple tint)
    gradient.addColorStop(0.5, '#0a1428'); // Middle
    gradient.addColorStop(1, '#1a3a2a'); // Bottom - buyers (green tint)
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, dimensions.width, dimensions.height);
    
    // Draw zone labels
    ctx.font = 'bold 16px monospace';
    ctx.textAlign = 'center';
    
    // Sellers zone (top)
    ctx.fillStyle = 'rgba(255, 107, 107, 0.7)';
    ctx.fillText('📤 SELLERS ZONE', dimensions.width / 2, MARGIN_TOP - 18);
    
    // Buyers zone (bottom)
    ctx.fillStyle = 'rgba(81, 233, 128, 0.7)';
    ctx.fillText('📥 BUYERS ZONE', dimensions.width / 2, dimensions.height - MARGIN_BOTTOM + 40);

    // The page header already shows the clock. A second badge here sat on the sellers.
    
    // Draw price scale on left
    ctx.fillStyle = '#888';
    ctx.font = '12px monospace';
    ctx.textAlign = 'right';
    
    const priceStep = Math.ceil(priceRange / 8);
    for (let price = minPrice; price <= maxPrice; price += priceStep) {
      const y = priceToY(price);
      
      // Price label
      ctx.fillStyle = '#888';
      ctx.fillText(`$${price}`, MARGIN_LEFT - 10, y + 4);
      
      // Grid line
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(MARGIN_LEFT, y);
      ctx.lineTo(dimensions.width - MARGIN_RIGHT, y);
      ctx.stroke();
    }
    
    // Draw store price lines
    // Store SELL price (ceiling - top)
    const storeSellY = priceToY(storeSellPrice);
    ctx.strokeStyle = '#FF6B6B';
    ctx.lineWidth = 3;
    ctx.setLineDash([10, 5]);
    ctx.beginPath();
    ctx.moveTo(MARGIN_LEFT, storeSellY);
    ctx.lineTo(dimensions.width - MARGIN_RIGHT, storeSellY);
    ctx.stroke();
    ctx.setLineDash([]);
    
    ctx.fillStyle = '#FF6B6B';
    ctx.font = 'bold 11px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(`STORE SELLS @ $${storeSellPrice}`, MARGIN_LEFT + 10, storeSellY - 8);
    ctx.fillStyle = '#888';
    ctx.font = '10px monospace';
    ctx.fillText(storeStock > 0 ? `(${storeStock} in stock)` : '(sold out)', MARGIN_LEFT + 180, storeSellY - 8);
    
    // Store BUY price (floor - bottom)
    const storeBuyY = priceToY(storeBuyPrice);
    ctx.strokeStyle = '#51E980';
    ctx.lineWidth = 3;
    ctx.setLineDash([10, 5]);
    ctx.beginPath();
    ctx.moveTo(MARGIN_LEFT, storeBuyY);
    ctx.lineTo(dimensions.width - MARGIN_RIGHT, storeBuyY);
    ctx.stroke();
    ctx.setLineDash([]);
    
    ctx.fillStyle = '#51E980';
    ctx.font = 'bold 11px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(`STORE BUYS @ $${storeBuyPrice}`, MARGIN_LEFT + 10, storeBuyY + 16);

    ctx.fillStyle = 'rgba(255, 107, 107, 0.12)';
    ctx.fillRect(MARGIN_LEFT, MARGIN_TOP, dimensions.width - MARGIN_LEFT - MARGIN_RIGHT, Math.max(0, storeSellY - MARGIN_TOP));
    ctx.fillStyle = 'rgba(120, 140, 255, 0.12)';
    ctx.fillRect(
      MARGIN_LEFT,
      storeBuyY,
      dimensions.width - MARGIN_LEFT - MARGIN_RIGHT,
      Math.max(0, dimensions.height - MARGIN_BOTTOM - storeBuyY),
    );
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.font = '10px monospace';
    ctx.textAlign = 'right';
    ctx.fillText('ABSTAIN', dimensions.width - MARGIN_RIGHT - 8, storeSellY - 8);
    ctx.fillText('ABSTAIN', dimensions.width - MARGIN_RIGHT - 8, storeBuyY + 16);
    
    // Calculate player X positions
    const arenaWidth = dimensions.width - MARGIN_LEFT - MARGIN_RIGHT;
    const playerSpacing = arenaWidth / (players.length + 1);
    
    // First pass: Draw horizontal lines for each player
    players.forEach((player, index) => {
      const pos = playerPositions.get(player.playerId);
      if (!pos) return;
      if (player.role === 'undecided') return; // Don't draw line for undecided players
      
      const x = MARGIN_LEFT + playerSpacing * (index + 1);
      const y = priceToY(pos.current);
      
      // Get player color
      const playerColor = PLAYER_COLORS[player.color] || '#888';
      
      const isInTrade = playerIsTrading(player.playerId, pos.current, player.role);
      
      // Draw horizontal dotted line across the arena
      ctx.strokeStyle = playerColor;
      ctx.lineWidth = isInTrade ? 4 : 2;
      ctx.setLineDash([8, 4]);
      ctx.globalAlpha = isInTrade ? 1.0 : 0.6;
      ctx.beginPath();
      ctx.moveTo(MARGIN_LEFT, y);
      ctx.lineTo(dimensions.width - MARGIN_RIGHT, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1.0;
    });
    
    // Second pass: Draw player avatars on top of lines
    players.forEach((player, index) => {
      const pos = playerPositions.get(player.playerId);
      if (!pos) return;
      
      const x = MARGIN_LEFT + playerSpacing * (index + 1);
      const priceY = priceToY(pos.current);
      // Sellers stand above their price, and above the store sell line.
      // A picture centered on that line hangs through it into the buyers' side.
      const sellerLift = 36;
      const y = player.role === 'seller'
        ? Math.min(priceY, storeSellY) - sellerLift
        : priceY;
      
      // Player color based on role
      const isCurrentPlayer = player.playerId === currentPlayerId;
      const playerColor = PLAYER_COLORS[player.color] || '#888';
      let roleColor = '#888'; // Undecided
      if (player.role === 'seller') roleColor = '#FF6B6B';
      else if (player.role === 'buyer') roleColor = '#51E980';
      
      const isInTrade = playerIsTrading(player.playerId, pos.current, player.role);
      
      // Draw player icon
      const playerIcon = playerIconImages.get(player.playerNumber);
      const iconSize = 48;
      
      // Draw glow effect if in trade
      if (isInTrade) {
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 20;
      }
      
      if (playerIcon) {
        ctx.drawImage(
          playerIcon,
          x - iconSize / 2,
          y - iconSize / 2,
          iconSize,
          iconSize
        );
      } else {
        // Fallback circle
        ctx.fillStyle = playerColor;
        ctx.beginPath();
        ctx.arc(x, y, 20, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.fillStyle = '#000';
        ctx.font = 'bold 12px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`P${player.playerNumber}`, x, y);
      }
      
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      
      // Highlight current player
      if (isCurrentPlayer) {
        ctx.strokeStyle = '#00FFFF';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(x, y, 28, 0, Math.PI * 2);
        ctx.stroke();
      }
      
      // Role indicator ring
      ctx.strokeStyle = roleColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, 24, 0, Math.PI * 2);
      ctx.stroke();
      
      const stock = Number(player.resources?.[resource] || 0);
      const previousStock = lastStockRef.current.get(player.playerId);
      if (previousStock != null && previousStock !== stock) {
        countFlashRef.current.set(player.playerId, { delta: stock - previousStock, at: Date.now() });
      }
      lastStockRef.current.set(player.playerId, stock);
      const flash = countFlashRef.current.get(player.playerId);
      const flashOn = !!flash && Date.now() - flash.at < 700;
      const priceLabel = `$${Math.round(pos.current)}`;
      const countLabel = `${stock}`;
      const flashLabel = flashOn && flash ? (flash.delta > 0 ? `+${flash.delta}` : `${flash.delta}`) : '';
      const seller = player.role === 'seller';
      const buyer = player.role === 'buyer';

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const name = (player.username || 'Player').trim().split(/\s+/)[0];
      const shortName = name.length > 9 ? `${name.slice(0, 8)}…` : name;
      ctx.font = '10px monospace';
      const nameWidth = ctx.measureText(shortName).width;
      const nameX = Math.min(dimensions.width - 8 - nameWidth / 2, Math.max(8 + nameWidth / 2, x));
      const nameY = seller ? y - 78 : buyer ? y - 52 : y + 48;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
      ctx.fillRect(nameX - nameWidth / 2 - 4, nameY - 7, nameWidth + 8, 14);
      ctx.fillStyle = '#FFF';
      ctx.fillText(shortName, nameX, nameY);

      if (seller || buyer) {
        const priceY = seller ? y - 34 : y + 36;
        const countY = seller ? y - 50 : y + 52;
        ctx.font = 'bold 13px monospace';
        ctx.fillStyle = isInTrade ? '#FFD700' : roleColor;
        ctx.fillText(priceLabel, x, priceY);
        ctx.font = 'bold 12px monospace';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(countLabel, x - (flashLabel ? 10 : 0), countY);
        if (flashLabel) {
          ctx.fillStyle = flash && flash.delta > 0 ? '#51E980' : '#FF6B6B';
          ctx.font = 'bold 12px monospace';
          ctx.fillText(flashLabel, x + 16, countY);
        }
      } else {
        ctx.font = 'bold 12px monospace';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(countLabel, x, y - 40);
      }
    });
    
    // Draw click hint
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '12px monospace';
    ctx.textAlign = 'center';
    if (phase === 'role-selection') {
      ctx.fillText('Click TOP to SELL or BOTTOM to BUY', dimensions.width / 2, dimensions.height - 15);
    } else if (phase === 'trading') {
      ctx.fillText('Click to move. Outside the store lines, you sit this round out.', dimensions.width / 2, dimensions.height - 15);
    }
    
  }, [dimensions, players, playerPositions, priceToY, minPrice, maxPrice, priceRange,
      storeBuyPrice, storeSellPrice, storeStock, currentPlayerId,
      playerIconImages, phase, timeRemaining, resource]);
  
  // Handle canvas click
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    // Check if click is within the arena
    if (y < MARGIN_TOP || y > dimensions.height - MARGIN_BOTTOM) {
      return;
    }
    
    // Convert Y to price
    const price = Math.max(minPrice, Math.min(maxPrice, yToPrice(y)));
    const clampedPrice = price;
    
    // Determine if click is in top or bottom half
    const middleY = (MARGIN_TOP + dimensions.height - MARGIN_BOTTOM) / 2;
    const isTopHalf = y < middleY;
    
    console.log(`🎯 Clicked at price: $${clampedPrice}, isTopHalf: ${isTopHalf}`);
    onArenaClick(clampedPrice, isTopHalf);
  }, [dimensions.height, yToPrice, minPrice, maxPrice, onArenaClick]);
  
  return (
    <div className="auction-arena" ref={containerRef}>
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        onClick={handleCanvasClick}
        className="auction-canvas"
      />
    </div>
  );
}
